import { action, makeObservable, observable, reaction } from 'mobx';

import { ProductConfigurationData } from '../../../data/model';
import Logger from '../../../shared/Logger';
import { RawConfiguratorState } from '../RawConfigurator/RawConfiguratorState';

export class ConfigurationRestorerState {
  @observable
  public restoreIncomplete: boolean = false;

  @observable
  public configurationCodeToRestore: string = '';

  constructor() {
    makeObservable(this);
  }

  @action
  public setConfigurationCodeToRestore(code: string) {
    this.configurationCodeToRestore = code;
  }

  private setRestoreIncomplete(incomplete: boolean) {
    this.restoreIncomplete = incomplete;
  }

  private onRestoreIncomplete(state: RawConfiguratorState) {
    this.setRestoreIncomplete(true);
    reaction(
      () => state.selectedFeatures?.length,
      () => {
        this.setRestoreIncomplete(false);
      },
    );
  }

  public async restore(
    state: RawConfiguratorState,
    configurationCode: string,
    configuration: ProductConfigurationData,
  ) {
    this.setConfigurationCodeToRestore(configurationCode);

    while (!state.configurationCompleted) {
      const featureSelection = state.selectedFeatures.last();

      if (!featureSelection) {
        break;
      }

      if (featureSelection.feature.includeInConfigurationCode) {
        const featureCode = featureSelection.feature.code;
        const optionCode = configuration.options.find((x) => x.componentCode === featureCode)?.variationCode;

        if (!optionCode) {
          Logger.warn(
            `Unable to restore the raw configuration ${configurationCode}. Feature ${featureCode} not found in the configuration.`,
          );
          this.onRestoreIncomplete(state);
          break;
        }

        const selectionRestored = await featureSelection.tryRestoreSelection(optionCode);
        if (!selectionRestored) {
          Logger.warn(
            `Unable to restore the raw configuration ${configurationCode}. Couldn't restore option ${optionCode} for feature ${featureCode}.`,
          );
          this.onRestoreIncomplete(state);
          break;
        }
      }

      if (state.canMoveNext) {
        state.populateNextFeatureSelection();
      } else {
        break;
      }
    }
  }
}
